"use client";

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  BarChart3,
  Users,
  Calendar,
  Download,
  Edit,
  Trash2,
  Search,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { SubmissionEditor } from "./submission-editor";
import PropTypes from "prop-types";
import "./form-report.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Grid,
  Paper,
  Card as MUICard,
  Tooltip,
  Button as MUIButton,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IconTableExport } from "@tabler/icons-react";
import handleExport from "utils/exportData";
import Footer from "examples/Footer";

export function FormReport({ formId, onBack, onEditForm, onDeleteForm, onUpdateSubmission }) {
  const [form, setForm] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [filters, setFilters] = useState({});
  const [originalSubmissions, setOriginalSubmissions] = useState([]);
  const [displayedSubmissions, setDisplayedSubmissions] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [userSummary, setUserSummary] = useState(null);
  const [formStats, setFormStats] = useState(null);

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  useEffect(() => {
    if (!meData?.user_id) return;

    const fetchUserSummary = async () => {
      try {
        let res;
        if (meData.user_id === 1) {
          // Admin: fetch all users summary
          res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/form/allCount`, {
            withCredentials: true,
          });
        } else {
          // Regular user: fetch only their summary
          res = await axios.get(
            `${process.env.REACT_APP_API_URL}/allforms/form/${meData.user_id}`,
            {
              withCredentials: true,
            }
          );
        }

        setUserSummary(res.data.data);
        console.log("User Summary:", res.data.data);
      } catch (err) {
        console.error("Error fetching user summary:", err);
      }
    };

    fetchUserSummary();
  }, [meData?.user_id]);

  useEffect(() => {
    if (!form?.id || !userSummary?.submissions_per_form) return;

    console.log("Form.id:", form.id);
    console.log(
      "Available form_ids:",
      userSummary?.submissions_per_form?.map((f) => f.form_id) || []
    );
    // match form id with submissions_per_form
    const matched = userSummary.submissions_per_form.find(
      (f) => String(f.form_id) === String(form.id)
    );

    setFormStats(matched || null);
    console.log("Form Stats:", matched);
  }, [form, userSummary]);

  const handleSaveSubmission = (updatedSubmission) => {
    if (editingSubmission) {
      onUpdateSubmission(form.id, editingSubmission.index, updatedSubmission);
      setEditingSubmission(null);
    }
  };

  const handleEditClick = async ({ id: formId, created_by, submission_id }) => {
    try {
      setLoading(true);

      // Pass only formId, created_by, and created_at to backend
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/submissions/${formId}`, {
        params: { created_by, submission_id },
        withCredentials: true,
      });

      console.log("Response:", response);
      const result = response.data;

      if (result.status === 200) {
        // You can directly use result.data (form + filtered submission)
        setEditingSubmission({
          form: result.data, // form details
          submission: result.data.submissions, // submission filtered by backend
        });
      } else {
        console.error("Failed to fetch submission");
      }
    } catch (error) {
      console.error("Error fetching submission:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    if (!form?.fields) return [];

    return [
      ...(meData?.user_id === 1
        ? [
            {
              accessorKey: "district_namee",
              header: "District Name",
            },
          ]
        : []),
      ...form.fields.map((field) => ({
        accessorKey: field.id, // e.g. field_1756798908765
        header: field.label, // e.g. "Name"
        accessorFn: (row) => row.values?.[field.id] ?? "",
      })),
      ...(meData?.user_id === 1
        ? [
            {
              accessorKey: "created_user",
              header: "Submitted User",
            },
          ]
        : []),
      {
        accessorKey: "created_at",
        header: "Submitted Date",
        Cell: ({ cell }) => dayjs(cell.getValue()).format("DD-MM-YYYY"),
      },
      {
        accessorKey: "edit",
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Click to edit">
              <span>
                <IconButton
                  key={row.original.submission_id} // submission id
                  onClick={() =>
                    handleEditClick({
                      id: form.id, // form ID
                      created_by: row.original.created_by,
                      submission_id: row.original.submission_id,
                    })
                  }
                  disabled={loading}
                  sx={{
                    color: "#f48c06",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                    transition: "background-color 0.3s, transform 0.3s",
                    "&:hover": {
                      backgroundColor: "#f48c06",
                      color: "#fff",
                      transform: "scale(1.05)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Edit sx={{ width: 20, height: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, [form, loading]);

  const paginatedData = useMemo(() => {
    let data = [...(displayedSubmissions || [])];

    // apply column filters
    columnFilters.forEach((filter) => {
      const { id, value } = filter;
      data = data.filter((row) =>
        String(row[id] || "")
          .toLowerCase()
          .includes(String(value).toLowerCase())
      );
    });

    // apply sorting
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      data.sort((a, b) => {
        let aVal = a[id];
        let bVal = b[id];

        // normalize strings to lowercase for case-insensitive comparison
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();

        if (aVal < bVal) return desc ? 1 : -1;
        if (aVal > bVal) return desc ? -1 : 1;
        return 0;
      });
    }

    return data; // ✅ return fully filtered & sorted data, no frontend pagination
  }, [sorting, columnFilters, displayedSubmissions]);

  const clearFilters = () => {
    const cleared = {};
    form?.fields?.forEach((f) => {
      cleared[f.id] = "";
    });
    cleared["district_name"] = "";
    setFilters(cleared);

    // Reset pagination to page 1
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });

    // Fetch fresh data from backend (page 1, no filters)
    fetchFilteredData({}, 0, pagination.pageSize);
  };

  const fetchFilteredData = async (appliedFilters, pageIndex = 0, pageSize = 10) => {
    setLoading(true);
    try {
      const formattedFilters = { ...appliedFilters };
      Object.keys(formattedFilters).forEach((key) => {
        const value = formattedFilters[key];
        if (!value) return;

        if (typeof value === "object" && value.$d) {
          formattedFilters[key] = dayjs(value).format("YYYY-MM-DD");
        } else if (value instanceof Date) {
          formattedFilters[key] = dayjs(value).format("YYYY-MM-DD");
        }
      });

      let url = "";
      if (meData?.user_id === 1) {
        url = `${process.env.REACT_APP_API_URL}/allforms/${formId}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/submissions/${formId}/${meData.user_id}`;
      }

      const res = await axios.get(url, {
        withCredentials: true,
        params: { ...formattedFilters, page: pageIndex + 1, limit: pageSize }, // ✅ send page & limit
      });

      if (res?.data?.data?.submissions) {
        setForm(res.data.data);
        setOriginalSubmissions(res.data.data.submissions);
        setDisplayedSubmissions(res.data.data.submissions);
        setRowCount(res.data.data.totalCount); // ✅ use totalCount from backend
      }
    } catch (err) {
      console.error("Error fetching filtered data:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFilteredData(filters, pagination.pageIndex, pagination.pageSize);
  }, [formId, pagination, meData?.user_id]);

  const handleExportData = async () => {
    try {
      let formattedData = [];

      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = form.submissions.filter((item, index) => rowSelection[index]);
      } else {
        // fetch ALL data directly for export
        const url =
          meData?.user_id === 1
            ? `${process.env.REACT_APP_API_URL}/allforms/${form.id}`
            : `${process.env.REACT_APP_API_URL}/submissions/${form.id}/${meData.user_id}`;

        const res = await axios.get(url, {
          withCredentials: true,
          params: { ...filters, page: 1, limit: 100000 }, // huge limit to get everything
        });

        formattedData = res.data?.data?.submissions || [];
      }

      if (formattedData.length === 0) {
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }

      const exportPayload = {
        ...form,
        submissions: formattedData,
      };

      await handleExport(exportPayload, "Form_Submissions_List_Data");

      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error("Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  return (
    <div className="form-report-container">
      {editingSubmission ? (
        <SubmissionEditor
          form={form}
          submission={editingSubmission.submission[0]}
          onSave={handleSaveSubmission}
          onCancel={() => setEditingSubmission(null)}
        />
      ) : (
        <>
          {/* Header */}
          <div className="form-report-header">
            <div className="form-report-header-left">
              <Button className="btn btn-outline btn-sm" variant="outline" onClick={onBack}>
                <ArrowLeft className="icon" />
                Back to Dashboard
              </Button>
              {/* <div>
            <h1 className="form-report-header-title">{form.title}</h1>
            <p className="form-report-header-desc">{form.description}</p>
          </div> */}
            </div>
            <div className="form-report-header-actions">
              {meData?.user_id === 1 && (
                <Button
                  className="btn btn-outline btn-sm"
                  variant="outline"
                  onClick={() => onEditForm(form.id)}
                >
                  <Edit className="icon" />
                  Edit Form
                </Button>
              )}
              {/* <Button className="btn btn-outline btn-sm" variant="outline" onClick={exportFormData}>
            <Download className="icon" />
            Export Report
          </Button> */}
              {meData?.user_id === 1 && (
                <Button
                  className="btn btn-danger btn-sm"
                  variant="destructive"
                  onClick={() => {
                    if (
                      confirm(
                        "Are you sure you want to delete this form? This action cannot be undone."
                      )
                    ) {
                      onDeleteForm(form.id);
                      onBack();
                    }
                  }}
                >
                  <Trash2 className="icon" />
                  Delete Form
                </Button>
              )}
            </div>
          </div>

          {/* Form Info */}
          <Card className="form-report-card">
            <CardHeader className="form-report-card-header">
              {/* <h1 className="form-report-header-title">{form.title}</h1>
              <p className="form-report-header-desc">{form.description}</p> */}
            </CardHeader>
            <CardContent>
              <div className="form-report-card-content">
                <div>
                  <Label className="form-report-card-content label">Form Title</Label>
                  <p className="form-report-card-content p">{form?.title}</p>
                </div>

                <div>
                  <Label className="form-report-card-content label">Description</Label>
                  <p className="form-report-card-content p">{form?.description}</p>
                </div>

                <div>
                  <Label className="form-report-card-content label">Created</Label>
                  <p className="form-report-card-content p">
                    {form?.createdAt && new Date(form.createdAt).toLocaleString()}
                  </p>
                </div>

                {form?.updatedAt && form?.updatedAt !== form?.createdAt && (
                  <div>
                    <Label className="form-report-card-content label">Last Updated</Label>
                    <p className="form-report-card-content p">
                      {new Date(form.updatedAt).toLocaleString()}
                    </p>
                  </div>
                )}

                <div>
                  <Label className="form-report-card-content label">Status</Label>
                  <Badge
                    className={`btn btn-sm ${
                      form?.stats?.total > 0 ? "btn-primary" : "btn-outline"
                    }`}
                  >
                    {form?.stats?.total > 0 ? "Active" : "No responses"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Analytics Cards */}
          <div className="form-report-grid">
            <Card
              className="form-report-analytic-card"
              style={{ backgroundColor: "#35bbcab3", color: "white" }}
            >
              <CardHeader className="form-report-header-row">
                <CardTitle className="form-report-analytic-title">Total Submissions</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="form-report-analytic-value">
                  {formStats?.total_submissions ?? 0}
                </div>
                <p className="form-report-analytic-sub">All time responses</p>
              </CardContent>
            </Card>

            <Card
              className="form-report-analytic-card"
              style={{ backgroundColor: "#5fa55ab3", color: "white" }}
            >
              <CardHeader className="form-report-header-row">
                <CardTitle className="form-report-analytic-title">Today</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="form-report-analytic-value">
                  {formStats?.today_submissions ?? 0}
                </div>
                <p className="form-report-analytic-sub">Submissions today</p>
              </CardContent>
            </Card>

            <Card
              className="form-report-analytic-card"
              style={{ backgroundColor: "#ec3432b3", color: "white" }}
            >
              <CardHeader className="form-report-header-row">
                <CardTitle className="form-report-analytic-title">This Week</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="form-report-analytic-value">
                  {formStats?.this_week_submissions ?? 0}
                </div>
                <p className="form-report-analytic-sub">Last 7 days</p>
              </CardContent>
            </Card>

            <Card
              className="form-report-analytic-card"
              style={{ backgroundColor: "#ff8a25b3", color: "white" }}
            >
              <CardHeader className="form-report-header-row">
                <CardTitle className="form-report-analytic-title">This Month</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="form-report-analytic-value">
                  {formStats?.this_month_submissions ?? 0}
                </div>
                <p className="form-report-analytic-sub">Last 30 days</p>
              </CardContent>
            </Card>
          </div>

          <MDBox mt={3} />
          <MUICard
            id="delete-account"
            sx={{
              mb: 3,
              border: "2px solid #2bc29c", // green border
              borderRadius: "10px", // optional, for rounded corners
            }}
          >
            <MDBox px={2} pt={0.5}>
              <Grid container spacing={3}>         
                {/* District (always first) */}
                {meData?.user_id === 1 && (
                  <Grid item size={{ xs: 12, md: 4 }}>
                    <MDAutocomplete
                      label="District"
                      name="district_name"
                      value={filters.district_name || ""}
                      options={districtData}
                      onChange={(newValue) => setFilters({ ...filters, district_name: newValue })}
                      isFilter
                    />
                  </Grid>
                )}

                {/* Dynamic fields */}
                {form &&
                  form.fields &&
                  form.fields.map((field) => (
                    <Grid key={field.id} item size={{ xs: 12, md: 4 }}>
                      {field.type === "text" || field.type === "email" || field.type === "phone" ? (
                        <MDTextField
                          name={field.id}
                          value={filters[field.id] || ""}
                          onChange={(e) => setFilters({ ...filters, [field.id]: e })}
                          placeholder={field.placeholder}
                          displayName={field.label}
                          inputType={field.type === "phone" ? "numeric" : "onlyString"}
                        />
                      ) : field.type === "date" ? (
                        <>
                          <MDTypography style={{ fontSize: "16px" }} color="dark">
                            {field.label}
                          </MDTypography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={filters[field.id] || null}
                              format="DD/MM/YYYY"
                              onChange={(value) => setFilters({ ...filters, [field.id]: value })}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  InputProps: {
                                    readOnly: true,
                                    sx: {
                                      fontSize: "14px",
                                      height: "45px",
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </>
                      ) : field.type === "select" || field.type === "radio" ? (
                        <MDAutocomplete
                          label={field.label}
                          name={field.id}
                          value={filters[field.id] || ""}
                          options={field.options?.map((opt) => ({ title: opt, value: opt })) || []}
                          onChange={(newValue) => setFilters({ ...filters, [field.id]: newValue })}
                          isFilter
                        />
                      ) : null}
                    </Grid>
                  ))}
              </Grid>
            </MDBox>

            {/* Action Buttons */}
            <MDBox p={2}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} md={2} textAlign="center">
                  <MDButton
                    onClick={() => fetchFilteredData(filters)}
                    sx={{
                      color: "white !important",
                      backgroundColor: "#1976d2",
                      "&:hover": { backgroundColor: "#155fa0" },
                      minWidth: "120px",
                    }}
                  >
                    Search
                  </MDButton>
                </Grid>
                <Grid item xs={12} md={2} textAlign="center">
                  <MDButton
                    variant="outlined"
                    onClick={clearFilters}
                    sx={{
                      color: "#b71c1c !important",
                      borderColor: "#b71c1c",
                      "&:hover": {
                        backgroundColor: "#fbe9e7",
                        borderColor: "#b71c1c",
                      },
                      minWidth: "120px",
                    }}
                  >
                    Cancel
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </MUICard>

          <Box>
            <Grid container spacing={5}>
              <Grid item size={{ xs: 12, md: 12 }} className="listTable">
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Paper
                    elevation={2}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      backgroundColor: "#fff",
                      boxShadow: "none",
                      p: 2,
                    }}
                  >
                    <MaterialReactTable
                      columns={columns}
                      data={paginatedData}
                      enableGlobalFilter={false}
                      enableFullScreenToggle={false}
                      enableDensityToggle={false}
                      initialState={{ showColumnFilters: false }}
                      layoutMode="grid"
                      enableRowNumbers
                      enableStickyHeader
                      manualFiltering
                      manualPagination
                      manualSorting
                      onPaginationChange={setPagination}
                      onSortingChange={setSorting}
                      onColumnFiltersChange={setColumnFilters}
                      rowCount={rowCount}
                      state={{ pagination, sorting, columnFilters, isLoading: loading }}
                      muiTableHeadProps={{
                        sx: {
                          padding: "0px", // won't apply directly to <thead>, but useful for general structure
                          backgroundColor: "#011F3A", // background on <thead> element itself
                        },
                      }}
                      enableColumnActions={false}
                      muiTableHeadCellProps={{
                        sx: {
                          backgroundColor: "#004030",
                          color: "#fff",
                          // fontSize: '16px',
                          fontWeight: "bold",
                          position: "sticky", // Make header sticky
                          top: 0, // Stick it at the top
                          zIndex: 1, // Ensure it stays
                          "& .MuiTableSortLabel-root": {
                            "& .MuiTableSortLabel-icon": {
                              color: "#fff !important",
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#fff !important",
                            fontSize: "12px",
                            "::placeholder": {
                              color: "#e0e0e0", // Default (blurred) placeholder color
                              opacity: 1,
                            },
                          },
                          "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                            color: "#ffffff", // Focused placeholder color
                          },
                          "& .MuiIconButton-root": {
                            color: "#fff",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "#e0e0e0",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#fff",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#fff",
                          },

                          "& .MuiCheckbox-root": {
                            color: "#fff",
                          },
                        },
                      }}
                      muiTableBodyCellProps={{
                        sx: {
                          padding: "8px", // reduce from default 16px
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                      muiTableBodyRowProps={({ row }) => ({
                        sx: {
                          backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                        },
                      })}
                      renderTopToolbarCustomActions={({ table }) => (
                        <Box
                          sx={{
                            display: "flex",
                            gap: "1rem",
                            p: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <Tooltip title={"Export data to excel sheet"} arrow>
                            <MUIButton
                              onClick={handleExportData}
                              sx={{
                                background:
                                  "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                                color: "#f4f4f4",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                transition: "background 0.3s, transform 0.3s",
                                "&:hover": {
                                  background:
                                    "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                                  transform: "scale(1.05)",
                                },
                                "&:active": {
                                  transform: "scale(0.95)",
                                },
                              }}
                              startIcon={<IconTableExport />}
                            >
                              {"Export To Excel"}
                            </MUIButton>
                          </Tooltip>
                        </Box>
                      )}
                    />
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Snackbar
            open={Boolean(message.status)}
            autoHideDuration={3000}
            onClose={() => setMessage({ status: "", title: "", msg: "" })}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setMessage({ status: "", title: "", msg: "" })}
              severity={message.status}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {message.msg}
            </Alert>
          </Snackbar>
          <Footer />
        </>
      )}
    </div>
  );
}

FormReport.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    submissions: PropTypes.arrayOf(
      PropTypes.shape({
        submittedAt: PropTypes.string.isRequired,
        updatedAt: PropTypes.string,
        data: PropTypes.object,
      })
    ),
    totalCount: PropTypes.number.isRequired, // <-- add this
    page: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  formId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onEditForm: PropTypes.func.isRequired,
  onDeleteForm: PropTypes.func.isRequired,
  onUpdateSubmission: PropTypes.func.isRequired,
};
